import React, { useEffect, useRef, useState } from 'react';
import { ApplicationIcon, Icon } from '@saviynt/design-system';
import { hierarchy } from 'd3-hierarchy';
import { select } from 'd3-selection';

import { type NodeData } from './GraphData';

import './StaticGraph.css';

interface StaticRadialGraphProps {
  data?: NodeData;
  onNodeClick: (name: string) => void;
}

/**
 * Static Radial Graph
 */
function StaticRadialGraph({ onNodeClick, data }: StaticRadialGraphProps) {
  const width = 1000;
  const height = 600;
  const svgRef = useRef<SVGSVGElement | null>(null);

  const [nodePositions, setNodePositions] = useState<
    { x: number; y: number; data: NodeData; level: number }[]
  >([]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const center = { x: width / 2, y: height / 2 };

    const svg = select(svgRef.current);

    svg.selectAll('*').remove();
    svg.attr('width', width).attr('height', height);

    const g = svg
      .append('g')
      .attr('transform', `translate(${center.x}, ${center.y})`);

    const root = hierarchy<NodeData>(data);

    const offset = 35;

    const adjustLine = (
      x1: number,
      y1: number,
      x2: number,
      y2: number,
      offsetStart = offset,
      offsetEnd = offset
    ) => {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const length = Math.sqrt(dx * dx + dy * dy);
      const ux = dx / length;
      const uy = dy / length;

      return {
        x1: x1 + ux * offsetStart,
        y1: y1 + uy * offsetStart,
        x2: x2 - ux * offsetEnd,
        y2: y2 - uy * offsetEnd,
      };
    };

    const firstLevel = root.children || [];
    const angleStep = (2 * Math.PI) / firstLevel.length;
    const radiusFirstLevel = 180;
    const radiusSecondLevel = 120;

    const nodeList: { x: number; y: number; data: NodeData; level: number }[] =
      [];

    nodeList.push({
      x: center.x,
      y: center.y,
      data: root.data,
      level: 0, // Root
    });

    // First-level nodes
    firstLevel.forEach((node, i) => {
      const angle = i * angleStep - Math.PI / 2;
      const x = center.x + radiusFirstLevel * Math.cos(angle);
      const y = center.y + radiusFirstLevel * Math.sin(angle);

      nodeList.push({ x, y, data: node.data, level: 1 });

      // Second-level nodes
      const children = node.children || [];
      const childAngleSpread = Math.PI * 1.25;
      const angleStart = angle - childAngleSpread / 2;

      children.forEach((child, j) => {
        const angleStepChild =
          children.length > 1 ? childAngleSpread / (children.length - 1) : 0;
        const childAngle = angleStart + j * angleStepChild;
        const cx = x + radiusSecondLevel * Math.cos(childAngle);
        const cy = y + radiusSecondLevel * Math.sin(childAngle);

        nodeList.push({ x: cx, y: cy, data: child.data, level: 2 });

        const childLine = adjustLine(x, y, cx, cy);

        g.append('line')
          .attr('x1', childLine.x1 - center.x)
          .attr('y1', childLine.y1 - center.y)
          .attr('x2', childLine.x2 - center.x)
          .attr('y2', childLine.y2 - center.y)
          .attr('stroke', '#8C9DB8')
          .attr('stroke-width', 2);
      });

      const rootLine = adjustLine(center.x, center.y, x, y, 50, 35);

      g.append('line')
        .attr('x1', rootLine.x1 - center.x)
        .attr('y1', rootLine.y1 - center.y)
        .attr('x2', rootLine.x2 - center.x)
        .attr('y2', rootLine.y2 - center.y)
        .attr('stroke', '#8C9DB8')
        .attr('stroke-width', 2);
    });

    setNodePositions(nodeList);
  }, [data]);

  return (
    <div className='graph-container'>
      <svg ref={svgRef} className='graph-svg' />
      {nodePositions.map(({ x, y, data: nodeData, level }, i) => {
        const isRoot = level === 0;
        const isFirstLevel = level === 1;

        let size: number;
        let iconSize: 'xxLarge' | 'large' | 'medium';

        if (isRoot) {
          size = 66;
          iconSize = 'xxLarge';
        } else if (isFirstLevel) {
          size = 55;
          iconSize = 'large';
        } else {
          size = 40;
          iconSize = 'medium';
        }

        const backgroundColor = isFirstLevel
          ? '#426498'
          : 'var(--color-background-primary-subtle)';

        return (
          <div
            key={nodeData.id ?? `node-${i}`}
            style={{
              position: 'absolute',
              left: x - size / 2,
              top: y - size / 2,
              width: size,
              height: size,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto',
              border: isRoot
                ? ''
                : `2px solid var(--color-border-secondary-subtlest)`,
              borderRadius: '50%',
              background: backgroundColor,
              cursor: !isRoot ? 'pointer' : undefined,
              zIndex: 3,
            }}
            tabIndex={isRoot ? -1 : 0}
            role={isRoot ? undefined : 'button'}
            onClick={
              isRoot
                ? undefined
                : () => {
                    onNodeClick(nodeData.id ?? '');
                  }
            }
            onKeyDown={
              isRoot
                ? undefined
                : (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onNodeClick(nodeData.id ?? '');
                    }
                  }
            }>
            {nodeData.appName ? (
              <ApplicationIcon
                logo={nodeData.appName}
                iconColor={
                  level === 1
                    ? '--color-foreground-neutral-inverse'
                    : '--color-foreground-secondary-medium'
                }
                size={iconSize}
              />
            ) : (
              nodeData.icon && (
                <Icon
                  kind={nodeData.icon}
                  color={
                    level === 1
                      ? '--color-foreground-neutral-inverse'
                      : '--color-foreground-secondary-medium'
                  }
                  size={iconSize}
                />
              )
            )}
          </div>
        );
      })}
    </div>
  );
}

StaticRadialGraph.defaultProps = {
  data: undefined,
};

export default StaticRadialGraph;
